// useAudioOfflinePrefetch — проактивная загрузка готового аудио в офлайн-кэш.
//
// Правило из VISION §6.6: **готовый аудиофайл оказывается на устройстве ДО
// того, как о нём вспомнили.** Не по открытию страницы, не по нажатию play, не
// по кнопке «скачать» — по СОБЫТИЮ ГОТОВНОСТИ. Постоянный слушатель (аудио —
// его канал, а значит он в дороге) не должен обнаружить, что файла нет именно
// в метро.
//
// Матрица решений (§6.6):
//   Wi-Fi / не-metered   → качаем сразу
//   мобильный интернет   → только если пользователь снял «Только по Wi-Fi»
//   saveData             → не качаем никогда
//   приложение свернули  → догружаем при ВОЗВРАТЕ (`onAppVisibilityChange`),
//                          а не в background: iOS убьёт задачу WebView.

import { networkCapacitorAdapter } from "@studyhub/platform/adapters/capacitor/network.capacitor";
import { networkWebAdapter } from "@studyhub/platform/adapters/web/network.web";
import type { NetworkPort } from "@studyhub/platform/ports";
import { useEffect, useRef } from "react";
import { useSession } from "@/hooks/use-session";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { onAppVisibilityChange } from "@/lib/app-visible";
import { audioOfflineCache } from "@/lib/audio-offline-cache";
import { track } from "@/lib/observability/analytics";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

// Тот же паттерн выбора адаптера, что в `lib/app-visible.ts`: прямой импорт +
// compile-time гейт (conditional exports в проекте не настроены).
const network: NetworkPort = IS_CAPACITOR ? networkCapacitorAdapter : networkWebAdapter;

/** Больше трёх заходов на один файл не делаем — иначе очередь крутится вечно. */
const MAX_ATTEMPTS = 3;

interface OfflineSettingsRow {
	audio_offline_prefetch: number | null;
	audio_offline_wifi_only: number | null;
}

/** `null` = строка ещё не синкнулась; дефолт колонки в БД — включено. */
function boolSetting(value: number | null | undefined): boolean {
	return value == null ? true : value === 1;
}

/**
 * Монтируется один раз на приложение (см. `use-mini-player.ts` — глобальный
 * аудио-хост). Ничего не рендерит и ничего не возвращает.
 */
export function useAudioOfflinePrefetch(): void {
	const { user } = useSession();
	const userId = user?.id;

	// Настройки читаем из PowerSync, а не из локального стейта: тумблер в
	// «Настройки → Офлайн» пишется туда же и обязан РЕАЛЬНО влиять на хук.
	const settings = usePowerSyncQuery<OfflineSettingsRow>(
		`SELECT audio_offline_prefetch, audio_offline_wifi_only FROM users WHERE id = ? LIMIT 1`,
		userId ? [userId] : [],
	);
	const row = settings.data?.[0];
	const prefetchEnabled = boolSetting(row?.audio_offline_prefetch);
	const wifiOnly = boolSetting(row?.audio_offline_wifi_only);

	// Актуальные значения читаются внутри async-цикла, поэтому держим их в ref:
	// иначе очередь, стартовавшая до смены тумблера, доработает по старым.
	const enabledRef = useRef(prefetchEnabled);
	enabledRef.current = prefetchEnabled;
	const wifiOnlyRef = useRef(wifiOnly);
	wifiOnlyRef.current = wifiOnly;

	const pendingRef = useRef(new Map<string, number>());
	const runningRef = useRef(false);

	const drainRef = useRef<() => void>(() => {});
	drainRef.current = () => {
		void drain(pendingRef, runningRef, enabledRef.current, wifiOnlyRef.current);
	};

	// 1. Событие готовности — единственный триггер постановки в очередь.
	useSocketEvent("audio-recap:ready", ({ recapId }) => {
		if (!recapId || pendingRef.current.has(recapId)) return;
		pendingRef.current.set(recapId, 0);
		drainRef.current();
	});

	// 2. Возврат в приложение — догружаем то, что не успели.
	useEffect(() => {
		return onAppVisibilityChange(() => {
			drainRef.current();
		});
	}, []);

	// 3. Смена настроек: включили предзагрузку → сразу разбираем накопленное.
	useEffect(() => {
		if (prefetchEnabled) drainRef.current();
	}, [prefetchEnabled]);
}

/** Можно ли качать прямо сейчас. `unknown` считаем разрешённым — см. web-адаптер. */
async function canDownloadNow(wifiOnly: boolean): Promise<boolean> {
	if (await network.isSaveData()) return false;
	const type = await network.getConnectionType();
	if (type === "none") return false;
	if (type === "cellular" && wifiOnly) return false;
	return true;
}

async function drain(
	pendingRef: { current: Map<string, number> },
	runningRef: { current: boolean },
	enabled: boolean,
	wifiOnly: boolean,
): Promise<void> {
	const pending = pendingRef.current;
	if (!enabled) {
		// Настройка выключена — очередь не копим, чтобы она не «выстрелила»
		// пачкой загрузок через неделю после включения.
		pending.clear();
		return;
	}
	if (runningRef.current || pending.size === 0) return;
	if (!(await canDownloadNow(wifiOnly))) return;

	runningRef.current = true;
	try {
		for (const [recapId, attempts] of [...pending]) {
			if (!(await canDownloadNow(wifiOnly))) return;
			const cached = await audioOfflineCache.ensureCached(recapId);
			// `cached` — факт наличия файла ПОСЛЕ попытки, а не постановка в очередь.
			track("audio_prefetched", { reason: "preference", cached });
			if (cached || attempts + 1 >= MAX_ATTEMPTS) pending.delete(recapId);
			else pending.set(recapId, attempts + 1);
		}
	} finally {
		runningRef.current = false;
	}
}
